import { createPortal } from 'react-dom';
import { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import MonthGrid from './MonthGrid';
import CalendarToolbar from './CalendarToolbar';
import calendarEngine from '../../CalendarWidget/Core/CalendarEngine';

/**
 * Calendar popup — 2 months side by side, prev/next navigation, readonly.
 * Closes on outside click or Escape.
 * Reads events once from calendarEngine.getUpcoming() at render time — no
 * subscription, this is a readonly date browser the user opens/glances at/
 * closes, not a live ticking widget like the floating cards.
 * @param {object} props
 * @param {{ top?: number, bottom?: number, right: number }} props.anchorPos - top opens below anchor, bottom opens above anchor
 * @param {function} props.onClose
 */
export default function CalendarPopup({ anchorPos, onClose }) {
    const today = new Date();
    const [offset, setOffset] = useState(0); // months offset from current month
    const popupRef = useRef(null);
    const events = calendarEngine.getUpcoming();

    // Derive the two visible months from current offset
    const dateA = new Date(today.getFullYear(), today.getMonth() + offset - 1, 1);
    const dateB = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    const dateC = new Date(today.getFullYear(), today.getMonth() + offset + 1, 1);

    // Close on outside click — delayed to avoid closing on the triggering click
    useEffect(() => {
        const handleClick = (e) => {
            if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
        };
        const t = setTimeout(() => document.addEventListener('mousedown', handleClick), 50);
        return () => {
            clearTimeout(t);
            document.removeEventListener('mousedown', handleClick);
        };
    }, [onClose]);

    // Close on Escape
    useEffect(() => {
        const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [onClose]);

    const popup = (
        <Box
            ref={popupRef}
            onMouseLeave={onClose}
            sx={{
                position: 'fixed',
                top: anchorPos.top,
                bottom: anchorPos.bottom,
                right: anchorPos.right,
                zIndex: 10000,
                bgcolor: '#0d1f33',
                border: '0.5px solid #294766',
                borderRadius: '6px',
                boxShadow: '0 8px 28px rgba(0,0,0,0.6)',
                p: '12px 14px 14px',
                userSelect: 'none',
            }}
        >
            <CalendarToolbar
                offset={offset}
                onPrev={() => setOffset((o) => o - 1)}
                onNext={() => setOffset((o) => o + 1)}
                onToday={() => setOffset(0)}
                onClose={onClose}
            />

            {/* CHANGED — 3 maanden: vorige, huidige, volgende */}
            <Box sx={{ display: 'flex', gap: '20px' }}>
                <MonthGrid year={dateA.getFullYear()} month={dateA.getMonth()} today={today} events={events} />
                <MonthGrid year={dateB.getFullYear()} month={dateB.getMonth()} today={today} events={events} />
                <MonthGrid year={dateC.getFullYear()} month={dateC.getMonth()} today={today} events={events} />
            </Box>
        </Box>
    );

    return createPortal(popup, document.body);
}
